/**
 * Works out what kind of phone and browser ANNA is open in, so the "Get the
 * ANNA app" card can show the right steps. Pure functions of the user agent,
 * so they can be tested with sample strings.
 */

// Apps whose built-in browser can't put a website on the phone. Order matters:
// Messenger's user agent also carries Facebook's markers.
const IN_APP_BROWSERS = [
  { name: "Messenger", test: /FB_IAB\/(MESSENGER|Orca-Android)|FBAN\/(Messenger|MessengerForiOS|MessengerLiteForiOS)/i },
  { name: "Facebook", test: /FBAN\/|FBAV\/|FB_IAB\/|FBIOS|FB4A/ },
  { name: "Instagram", test: /Instagram/ },
  { name: "TikTok", test: /musical_ly|Bytedance|TikTok/i },
  { name: "LINE", test: /\bLine\//i },
  { name: "Snapchat", test: /Snapchat/i },
  { name: "X", test: /Twitter/i },
];

// Development only: sessionStorage "annaApp.previewUA" makes a desktop browser pass for that phone.
const previewUA = () => {
  if (process.env.NODE_ENV !== "development") return null;
  try {
    return window.sessionStorage.getItem("annaApp.previewUA");
  } catch (error) {
    return null;
  }
};

export function detectPlatform(
  ua = previewUA() || (typeof navigator !== "undefined" ? navigator.userAgent : ""),
  { platform = typeof navigator !== "undefined" ? navigator.platform : "", maxTouchPoints = typeof navigator !== "undefined" ? navigator.maxTouchPoints : 0 } = {},
) {
  // iPads ask for desktop sites and say "Macintosh"; only the touch screen gives them away.
  const ipad = /iPad/.test(ua) || (/Macintosh/.test(ua) && platform === "MacIntel" && maxTouchPoints > 1);
  const ios = ipad || /iPhone|iPod/.test(ua);
  const android = !ios && /Android/i.test(ua);

  let inApp = null;
  if (ios || android) {
    const known = IN_APP_BROWSERS.find((app) => app.test.test(ua));
    if (known) inApp = known.name;
    // Other apps' built-in browsers: iPhone ones leave "Safari/" out, Android ones add "wv".
    else if (ios && !/Safari\//.test(ua)) inApp = "another app";
    else if (android && /;\s*wv\)/.test(ua)) inApp = "another app";
  }

  let iosBrowser = null;
  if (ios) {
    if (/CriOS\//.test(ua)) iosBrowser = "chrome";
    else if (/FxiOS\//.test(ua)) iosBrowser = "firefox";
    else if (/EdgiOS\//.test(ua)) iosBrowser = "edge";
    else if (/OPiOS\/|OPT\//.test(ua)) iosBrowser = "opera";
    else if (/Version\/[\d.]+.*Safari\//.test(ua)) iosBrowser = "safari";
    else iosBrowser = "other";
  }

  // Safari's own version ("Version/26.0"); iOS 26 stopped putting the real iOS version in the user agent.
  const safariMatch = iosBrowser === "safari" ? ua.match(/Version\/(\d+)/) : null;
  const safariVersion = safariMatch ? Number(safariMatch[1]) : null;

  return {
    ios,
    ipad,
    android,
    mobile: ios || android,
    inApp,
    iosBrowser,
    safariVersion,
    device: ipad ? "iPad" : ios ? "iPhone" : "phone",
    // The browser that can save ANNA to this phone
    browserName: ios ? "Safari" : "Chrome",
  };
}

/**
 * The variant of the "Get the ANNA app" card for this phone, or null when the
 * card has nothing to offer. `canPrompt` says Chrome's own install popup is ready.
 */
export function installVariant(info, canPrompt) {
  if (!info.mobile) return canPrompt ? "prompt" : null;
  if (info.inApp) return "in-app";
  if (info.ios) return info.iosBrowser === "safari" ? "ios-safari" : "ios-other";
  return canPrompt ? "prompt" : "android-menu";
}

/** A link that asks the phone to open `href` in its main browser instead of an app's built-in one. */
export function openInBrowserLink(info, href) {
  const url = new URL(href);
  // Safari's own link type (iOS 17+); apps hand it to Safari.
  if (info.ios) return `x-safari-${url.href}`;
  // An Android intent for Chrome, with the page's address minus the #part.
  if (info.android) {
    const scheme = url.protocol.replace(":", "");
    return `intent://${url.host}${url.pathname}${url.search}#Intent;scheme=${scheme};package=com.android.chrome;end`;
  }
  return url.href;
}

/** True when ANNA is running as the installed app rather than in a browser tab. */
export function isStandalone() {
  if (typeof window === "undefined") return false;
  return (
    window.matchMedia?.("(display-mode: standalone)").matches ||
    window.matchMedia?.("(display-mode: fullscreen)").matches ||
    window.navigator.standalone === true
  );
}
